import { EventEmitter, ListenerFn } from "eventemitter3";
import { ManagerConfig, SoundConfig } from "./Config";
import { SoundEventName } from "./SoundEventName";
import { SoundGroup } from "./SoundGroup";
import { BaseSound } from "./BaseSound";
import { Sound } from "./Sound";
import { Debug } from "./Debug";

export class SoundManager extends EventEmitter {
    /**
     */
    public static get usingWebAudio(): boolean {
        return Howler.usingWebAudio;
    }

    /**
     */
    public static get codecs() {
        return (Howler as any)._codecs;
    }

    public static getPreferedSrcFormat(urls: string[]): string | undefined {
        for (let i = 0; i < urls.length; i++) {
            const ext = urls[i].split(".").pop();
            if (ext && ext in this.codecs && this.codecs[ext]) {
                return urls[i];
            }
        }
    }

    /**
     */
    public config: ManagerConfig;

    public cache: { [key: string]: Sound } = {};

    public baseCache: { [key: string]: BaseSound } = {};

    public groups: { [key: string]: SoundGroup } = {
        main: new SoundGroup(this)
    };

    /**
     * Set the debug level of sound manager.
     */
    public static debug: Debug = Debug.Warn;

    /**
     */
    public static warn(...args: any[]) {
        if (this.debug >= Debug.Warn) console.warn(...args);
    }

    /**
     */
    public static info(...args: any[]) {
        if (this.debug >= Debug.Info) console.info(...args);
    }

    /**
     */
    private _paused = false;
    public get paused() {
        return this._paused;
    }
    public set paused(value) {
        SoundManager.info("SoundManager.paused =", value);
        this._paused = value;
        this.updatePauseState();
    }

    /**
     */
    private _blurred = false;
    public get blurred() {
        return this._blurred;
    }
    public set blurred(value) {
        SoundManager.info("SoundManager.blurred =", value);
        this._blurred = value;
        this.updateMuteState();
    }

    /**
     */
    private _muted = false;
    public get muted() {
        return this._muted || this.blurred;
    }
    public set muted(value) {
        SoundManager.info("SoundManager.muted =", value);
        this._muted = value;
        this.updateMuteState();
    }

    /**
     */
    public get loadedDuration(): number {
        let duration = 0;
        for (const key in this.cache) {
            if (this.cache.hasOwnProperty(key)) {
                const sound = this.cache[key];
                if (sound.state === "loaded") {
                    duration += sound.duration;
                }
            }
        }
        return duration;
    }

    /**
     */
    constructor() {
        super();
        Howler.autoSuspend = false;
    }

    /**
     *
     * @param event
     * @param cb
     * @param ctx
     */
    public on(event: SoundEventName, cb: ListenerFn, ctx: any): this {
        return super.on(event, cb, ctx);
    }

    /**
     *
     * @param event
     * @param cb
     * @param ctx
     */
    public once(event: SoundEventName, cb: ListenerFn, ctx: any): this {
        return super.on(event, cb, ctx);
    }

    /**
     */
    public codecSupported(extension: string) {
        return Howler.codecs(extension);
    }

    /**
     */
    public parseSoundConfig(config: ManagerConfig) {
        if (!config) new TypeError("SoundManager.parseSoundConfig: config undefined");
        this.config = config;

        for (let i = 0; i < config.groups.length; i++) {
            const groupConfig = config.groups[i];
            const group = new SoundGroup(this, groupConfig);
            this.groups[group.name] = group;
        }

        for (let i = 0; i < this.config.sounds.length; i++) {
            const soundConfig = this.config.sounds[i];
            const spriteGroup = soundConfig.spriteGroup;

            if (spriteGroup) {
                const spriteConfig = config.sprites[spriteGroup];
                let baseSound = this.baseCache[spriteGroup];
                if (!baseSound) {
                    baseSound = new BaseSound(this, spriteConfig);
                    this.baseCache[spriteGroup] = baseSound;
                }

                const sound = new Sound(this, baseSound, soundConfig);
                this.cache[sound.name] = sound;
            } else {
                if (soundConfig.src) {
                    soundConfig.src = soundConfig.src.replace(/\.[^/.]+$/, "");
                    let src = soundConfig.src;
                    soundConfig.formats = [];
                    for (let i = 0; i < this.config.formats.length; i++) {
                        const format = this.config.formats[i];
                        soundConfig.formats.push(src.concat("." + format));
                    }
                    const baseSound = new BaseSound(this, { urls: soundConfig.formats });

                    const sound = new Sound(this, baseSound, soundConfig);
                    const name = baseSound.getName();
                    this.baseCache[name] = baseSound;
                    this.cache[name] = sound;
                }
            }
        }
    }

    public parseAudioSprite() {}

    /**
     * Play a sound.
     * @param name Audio file source path.
     */
    public play(name: string, config?: SoundConfig): Sound | undefined {
        if (typeof name === "undefined" || name === null) {
            SoundManager.warn("SoundManager.play:", "name is undefined | null.", name);
        }
        if (typeof name === "string") {
            const sound = this.cache[name];
            if (!sound) SoundManager.warn("SoundManager.play:", "name not found -", name);
            else return sound.play(config);
        }
    }

    /**
     * Stop a sound.
     * @param [name] Audio file source path.
     */
    public stop(name?: string): Sound | undefined {
        if (typeof name === "undefined" || name === null) {
            SoundManager.info("SoundManager.stop:", "Stopping all sounds.");
            for (const key in this.cache) {
                if (this.cache.hasOwnProperty(key)) {
                    const sound = this.cache[key];
                    if (sound.playing) sound.stop();
                }
            }
        }
        if (typeof name === "string") {
            const sound = this.cache[name];
            if (!sound) SoundManager.warn("SoundManager.stop:", "name not found -", name);
            else return sound.stop();
        }
    }

    /**
     * Pause a sound.
     * @param name Audio file source path.
     */
    public pause(name: string): Sound | undefined {
        if (typeof name === "undefined") {
            SoundManager.warn("SoundManager.pause:", "name is undefined | null.", name);
        }
        if (typeof name === "string") {
            const sound = this.cache[name];
            if (!sound) SoundManager.warn("SoundManager.pause:", "name not found -", name);
            else {
                sound.paused = true;
                return sound;
            }
        }
    }

    /**
     *
     * @param name
     */
    public getGroup(name: string): SoundGroup | undefined {
        const group = this.groups[name];
        if (typeof group === "undefined" || group === null) {
            SoundManager.warn("SoundManager.getGroup:", "name is undefined | null.", name);
        }
        if (typeof name === "string") {
            if (!group) SoundManager.warn("SoundManager.getGroup:", "group not found -", name);
            else return group;
        }
    }

    public updatePauseState() {
        for (const key in this.cache) {
            if (this.cache.hasOwnProperty(key)) {
                this.cache[key].updatePauseState();
            }
        }
    }

    public updateMuteState() {
        for (const key in this.cache) {
            if (this.cache.hasOwnProperty(key)) {
                this.cache[key].updateMuteState();
            }
        }
    }

    /**
     */
    public stopGroup(name: string) {
        for (const key in this.cache) {
            if (this.cache.hasOwnProperty(key)) {
                const sound = this.cache[key];
                if (sound.group.name === name) {
                    sound.stop();
                }
            }
        }
    }

    emitPlay(sound: Sound, id: number) {
        this.emit("play", sound, id);
    }

    emitEnd(sound: Sound, id: number) {
        this.emit("end", sound, id);
    }

    emitStop(sound: Sound, id: number) {
        this.emit("stop", sound, id);
    }
}
